import { useMemo, useState } from 'react';
import { theme, FONT_STACK } from '@/analyses/_shared/theme';
import { WorldMap } from '@/components/WorldMap';
import { Legend } from '@/components/Legend';
import { StatsBar } from '@/components/StatsBar';
import { SearchFilter } from '@/components/SearchFilter';
import { CountryPanel } from '@/components/CountryPanel';
import { CompareView } from '@/components/CompareView';
import { RegionStrip } from '@/components/RegionStrip';
import { COUNTRIES, COUNTRY_BY_NAME } from '@/data/countries';
import { TIER_CONFIG, type Tier } from '@/data/tiers';

const MAX_COMPARE = 4;

function App() {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  const [tierFilter, setTierFilter] = useState<Tier | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [compareList, setCompareList] = useState<string[]>([]);

  // Count countries per tier for the stats bar / legend
  const tierCounts = useMemo(() => {
    const counts: Record<Tier, number> = {
      easy: 0,
      moderate: 0,
      restricted: 0,
      blocked: 0,
      'not-analyzed': 0,
    };
    for (const c of COUNTRIES) counts[c.tier] += 1;
    return counts;
  }, []);

  const hoveredCountryMeta = hoveredCountry
    ? COUNTRY_BY_NAME[hoveredCountry.toLowerCase()]
    : undefined;

  const highlightedIso2 = hoveredCountryMeta?.iso2 ?? null;

  const handleCountryClick = (name: string) => {
    setSelectedCountry(name);
  };

  const handleSelectFromSearch = (name: string) => {
    setSelectedCountry(name);
    setSearchQuery('');
  };

  const handleAddToCompare = (name: string) => {
    setCompareList((prev) => {
      if (prev.includes(name)) return prev;
      return [...prev, name].slice(-MAX_COMPARE);
    });
  };

  const handleRemoveFromCompare = (name: string) => {
    setCompareList((prev) => prev.filter((n) => n !== name));
  };

  const handleCloseCompare = () => setCompareList([]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: theme.DARK,
        color: theme.TEXT,
        fontFamily: FONT_STACK,
      }}
    >
      <header
        className="no-print"
        style={{
          padding: '14px 24px 12px',
          borderBottom: `1px solid ${theme.BORDER}`,
          background: theme.DARK2,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 12,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img
              src="/menta-logo-white.png"
              alt="menta tech"
              style={{ height: 28, width: 'auto', display: 'block' }}
            />
            <div
              style={{
                width: 1,
                height: 26,
                background: theme.BORDER,
                flexShrink: 0,
              }}
            />
            <div>
              <div
                style={{
                  color: theme.WHITE,
                  fontSize: 17,
                  fontWeight: 700,
                  letterSpacing: -0.2,
                }}
              >
                Global Compliance Map
              </div>
              <div
                style={{
                  color: theme.MUTED,
                  fontSize: 11,
                  marginTop: 1,
                  letterSpacing: 0.1,
                }}
              >
                Country-by-country resale regulatory analysis
              </div>
            </div>
          </div>
          <SearchFilter
            query={searchQuery}
            onQueryChange={setSearchQuery}
            onSelectCountry={handleSelectFromSearch}
          />
        </div>

        <div style={{ marginBottom: 8 }}>
          <StatsBar tierCounts={tierCounts} />
        </div>

        <Legend
          activeTier={tierFilter}
          onTierClick={setTierFilter}
          tierCounts={tierCounts}
        />

        <div style={{ marginTop: 10 }}>
          <RegionStrip onSelectRegion={(name) => setSelectedCountry(name)} />
        </div>
      </header>

      <main
        className="no-print"
        style={{
          flex: 1,
          position: 'relative',
          minHeight: 520,
          background: theme.DARK,
        }}
      >
        <WorldMap
          onCountryClick={handleCountryClick}
          onCountryHover={setHoveredCountry}
          filterTier={tierFilter}
          searchQuery={searchQuery}
          highlightedIso2={highlightedIso2}
        />

        {hoveredCountryMeta && (
          <div
            className="no-print"
            style={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              background: theme.CARD,
              border: `1px solid ${theme.BORDER}`,
              borderRadius: 10,
              padding: '12px 14px',
              minWidth: 240,
              maxWidth: 360,
              boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
              pointerEvents: 'none',
              zIndex: 40,
              fontFamily: FONT_STACK,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: TIER_CONFIG[hoveredCountryMeta.tier].color,
                  boxShadow: `0 0 0 2px ${theme.CARD}, 0 0 0 3px ${
                    TIER_CONFIG[hoveredCountryMeta.tier].color
                  }44`,
                }}
              />
              <span
                style={{
                  color: theme.WHITE,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {hoveredCountryMeta.name}
              </span>
              <span style={{ color: theme.MUTED, fontSize: 11 }}>
                {hoveredCountryMeta.iso2}
              </span>
            </div>
            <div
              style={{
                color: TIER_CONFIG[hoveredCountryMeta.tier].color,
                fontSize: 10.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.6,
              }}
            >
              {TIER_CONFIG[hoveredCountryMeta.tier].label}
            </div>
            {hoveredCountryMeta.summary && (
              <div
                style={{
                  color: theme.TEXT,
                  fontSize: 12,
                  marginTop: 6,
                  lineHeight: 1.5,
                }}
              >
                {hoveredCountryMeta.summary}
              </div>
            )}
            <div
              style={{
                color: theme.MUTED,
                fontSize: 10,
                marginTop: 8,
                fontStyle: 'italic',
              }}
            >
              Click to open full analysis
            </div>
          </div>
        )}
      </main>

      <CountryPanel
        countryName={selectedCountry}
        onClose={() => setSelectedCountry(null)}
        onOpenAnalysis={(name) => setSelectedCountry(name)}
        onAddToCompare={handleAddToCompare}
      />

      <CompareView
        countryNames={compareList}
        onRemove={handleRemoveFromCompare}
        onClose={handleCloseCompare}
        onOpenAnalysis={(name) => setSelectedCountry(name)}
      />
    </div>
  );
}

export default App;
